import React, { useState, useEffect, useCallback } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import Tooltip from '@mui/material/Tooltip';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Divider from '@mui/material/Divider';
import dayjs, { Dayjs } from 'dayjs';
import { DateRange } from '@mui/x-date-pickers-pro';
import ControlledDateRangePicker from 'shared/components/ControlledDateRangePicker';
import AdLibraryService from 'services/AdLibraryService';
import useSortableTable from 'shared/useSortableTable';
import { formatKeywordDate, keywordComparators } from 'shared/keywordSort';
import AdvertiserPagePicker from 'shared/components/AdvertiserPagePicker';
import SearchUrlPreview from 'shared/components/SearchUrlPreview';
import useKeywordSearchUrl from 'shared/useKeywordSearchUrl';
import AdvertiserPagesDialog from 'shared/components/AdvertiserPagesDialog';
import AdvertiserTargets from 'shared/components/AdvertiserTargets';
import Checkbox from '@mui/material/Checkbox';
import { Keyword, ScrapedAd, AnalysisPrompt } from './types';
import ScrapeProgressDialog from './ScrapeProgressDialog';
import AdDetailDialog from './AdDetailDialog';

const service = new AdLibraryService();

function toYMD(d: Date) { return d.toISOString().split('T')[0]; }
function daysAgo(n: number) { const d = new Date(); d.setDate(d.getDate() - n); return toYMD(d); }
function startOfYear() { return `${new Date().getFullYear()}-01-01`; }

const DATE_PRESETS: Array<{ label: string; from: string; to: string }> = [
  { label: 'All Time', from: '', to: '' },
  { label: 'Last Week', from: daysAgo(7), to: toYMD(new Date()) },
  { label: 'Last Month', from: daysAgo(30), to: toYMD(new Date()) },
  { label: 'Last 3 Months', from: daysAgo(90), to: toYMD(new Date()) },
  { label: 'This Year', from: startOfYear(), to: toYMD(new Date()) },
];

function formatDateRange(min: string | null, max: string | null): string {
  if (!min && !max) return 'All Time';
  if (min && max) return `${min} — ${max}`;
  if (min) return `From ${min}`;
  return `Until ${max}`;
}

export default function KeywordManager() {
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newDepth, setNewDepth] = useState(20);
  const [newDatePreset, setNewDatePreset] = useState('All Time');
  const [newDateFrom, setNewDateFrom] = useState('');
  const [newDateTo, setNewDateTo] = useState('');
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editKeyword, setEditKeyword] = useState<Keyword | null>(null);
  const [editKeywordText, setEditKeywordText] = useState('');
  const [editDepth, setEditDepth] = useState(20);
  const [editDatePreset, setEditDatePreset] = useState('Custom');
  const [editDateFrom, setEditDateFrom] = useState('');
  const [editDateTo, setEditDateTo] = useState('');
  const [editAutoTranscribe, setEditAutoTranscribe] = useState(false);
  const [editAutoAnalyze, setEditAutoAnalyze] = useState(false);
  const [editPromptIds, setEditPromptIds] = useState<string[] | null>(null);
  const [editPageIds, setEditPageIds] = useState<string[]>([]);
  const [editPageGroupIds, setEditPageGroupIds] = useState<string[]>([]);
  const [managingPages, setManagingPages] = useState(false);

  // Same preview the static tool shows, previewing a VIDEO search — the media
  // type is decided by which endpoint answers, not by anything sent here.
  const searchPreview = useKeywordSearchUrl(
    body => service.keywordSearchUrl(body),
    {
      keyword: editKeywordText,
      dateRangePreset: editDatePreset === 'Custom' ? undefined : editDatePreset,
      startDateMin: editDatePreset === 'Custom' ? editDateFrom : null,
      startDateMax: editDatePreset === 'Custom' ? editDateTo : null,
      pageIds: editPageIds,
      pageGroupIds: editPageGroupIds,
    },
    !!editKeyword,
  );
  const [prompts, setPrompts] = useState<AnalysisPrompt[]>([]);
  const [saving, setSaving] = useState(false);
  const [editAdsAll, setEditAdsAll] = useState<ScrapedAd[]>([]); // buffered ads (fetched 250 at a time)
  const [editAdsTotal, setEditAdsTotal] = useState(0);
  const [editAdsPage, setEditAdsPage] = useState(0);
  const [editAdsRowsPerPage, setEditAdsRowsPerPage] = useState(25);
  const [editAdsLoading, setEditAdsLoading] = useState(false);
  const [viewAdId, setViewAdId] = useState<string | null>(null);

  const { sorted, sortField, sortDir, handleSort } = useSortableTable<Keyword>(
    keywords, 'createdAt', 'desc', keywordComparators,
  );
  const { sorted: sortedEditAdsAll, sortField: editAdsSortField, sortDir: editAdsSortDir, handleSort: handleEditAdsSort } = useSortableTable<ScrapedAd>(editAdsAll, 'impressionRank');

  // Confirmation dialog state
  const [confirmFetchOpen, setConfirmFetchOpen] = useState(false);
  const [confirmFetchKeyword, setConfirmFetchKeyword] = useState<Keyword | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteKeyword, setConfirmDeleteKeyword] = useState<Keyword | null>(null);
  const [deleteUsage, setDeleteUsage] = useState<{ videoAdCount: number; staticAdCount: number } | null>(null);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const [confirmDeactivateKeyword, setConfirmDeactivateKeyword] = useState<Keyword | null>(null);

  // Per-keyword action loading state
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  // Scrape dialog state
  const [scrapeDialogOpen, setScrapeDialogOpen] = useState(false);
  const [scrapeInitialKeyword, setScrapeInitialKeyword] = useState<{ keyword: string; scrapeDepth: number; keywordId: string; startDateMin?: string | null; startDateMax?: string | null } | null>(null);

  const fetchKeywords = useCallback(async () => {
    setLoading(true);
    const data = await service.listKeywords();
    if (data) setKeywords(data.keywords);
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeywords(); }, [fetchKeywords]);

  useEffect(() => {
    service.listPrompts().then(data => { if (data) setPrompts(data.prompts); });
  }, []);

  const handleCreate = async () => {
    if (!newKeyword.trim()) return;
    await service.createKeyword({
      keyword: newKeyword.trim(),
      scrapeDepth: newDepth,
      startDateMin: newDateFrom || null,
      startDateMax: newDateTo || null,
    });
    setNewKeyword('');
    setNewDepth(20);
    setNewDatePreset('All Time');
    setNewDateFrom('');
    setNewDateTo('');
    setDialogOpen(false);
    fetchKeywords();
  };

  const handleDeleteClick = async (e: React.MouseEvent, kw: Keyword) => {
    e.stopPropagation();
    setConfirmDeleteKeyword(kw);
    setDeleteUsage(null);
    setConfirmDeleteOpen(true);
    // Best-effort: the warning stands on its own if this fails.
    setDeleteUsage(await service.keywordUsage(kw.keywordId));
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteKeyword) return;
    const id = confirmDeleteKeyword.keywordId;
    setConfirmDeleteOpen(false);
    setActionLoading(prev => ({ ...prev, [id]: true }));
    await service.deleteKeyword(id);
    setActionLoading(prev => ({ ...prev, [id]: false }));
    setConfirmDeleteKeyword(null);
    fetchKeywords();
  };

  const handleDeactivateClick = (e: React.MouseEvent, kw: Keyword) => {
    e.stopPropagation();
    setConfirmDeactivateKeyword(kw);
    setConfirmDeactivateOpen(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!confirmDeactivateKeyword) return;
    const id = confirmDeactivateKeyword.keywordId;
    setConfirmDeactivateOpen(false);
    setActionLoading(prev => ({ ...prev, [id]: true }));
    await service.updateKeyword(id, { isActive: false });
    setActionLoading(prev => ({ ...prev, [id]: false }));
    setConfirmDeactivateKeyword(null);
    fetchKeywords();
  };

  const handleActivate = async (e: React.MouseEvent, kw: Keyword) => {
    e.stopPropagation();
    setActionLoading(prev => ({ ...prev, [kw.keywordId]: true }));
    await service.updateKeyword(kw.keywordId, { isActive: true });
    setActionLoading(prev => ({ ...prev, [kw.keywordId]: false }));
    fetchKeywords();
  };

  const BATCH_SIZE = 250;

  const fetchEditAdsBatch = useCallback(async (keywordId: string, offset = 0, replace = true) => {
    setEditAdsLoading(true);
    const data = await service.listAds({ keywordId, limit: BATCH_SIZE, offset });
    if (data) {
      setEditAdsAll(prev => replace ? data.ads : [...prev, ...data.ads]);
      setEditAdsTotal(data.total);
    }
    setEditAdsLoading(false);
  }, []);

  const handleRowClick = (kw: Keyword) => {
    setEditKeyword(kw);
    setEditKeywordText(kw.keyword);
    setEditDepth(kw.scrapeDepth);
    setEditAutoTranscribe(kw.autoTranscribe);
    setEditAutoAnalyze(kw.autoAnalyze);
    setEditPromptIds(kw.analysisPromptIds);
    setEditPageIds(kw.pageIds || []);
    setEditPageGroupIds(kw.pageGroupIds || []);
    setEditDateFrom(kw.startDateMin || '');
    setEditDateTo(kw.startDateMax || '');
    setEditDatePreset(kw.dateRangePreset || (kw.startDateMin || kw.startDateMax ? 'Custom' : 'All Time'));
    setEditAdsPage(0);
    setEditAdsRowsPerPage(25);
    fetchEditAdsBatch(kw.keywordId, 0, true);
    setEditDialogOpen(true);
  };

  const handleEditAdsPageChange = (_: unknown, newPage: number) => {
    setEditAdsPage(newPage);
    // Fetch next batch if we're about to exceed the buffer
    const needed = (newPage + 1) * editAdsRowsPerPage;
    if (needed > editAdsAll.length && editAdsAll.length < editAdsTotal && editKeyword) {
      fetchEditAdsBatch(editKeyword.keywordId, editAdsAll.length, false);
    }
  };

  const handleEditAdsRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditAdsRowsPerPage(parseInt(e.target.value, 10));
    setEditAdsPage(0);
  };

  const sortedEditAds = sortedEditAdsAll.slice(editAdsPage * editAdsRowsPerPage, (editAdsPage + 1) * editAdsRowsPerPage);

  const handleEditSave = async () => {
    if (!editKeyword || !editKeywordText.trim()) return;
    setSaving(true);
    const isPreset = editDatePreset !== 'Custom' && DATE_PRESETS.some(p => p.label === editDatePreset);
    await service.updateKeyword(editKeyword.keywordId, {
      keyword: editKeywordText.trim(),
      scrapeDepth: editDepth,
      dateRangePreset: isPreset ? editDatePreset : null,
      startDateMin: isPreset ? null : (editDateFrom || null),
      startDateMax: isPreset ? null : (editDateTo || null),
      autoTranscribe: editAutoTranscribe,
      autoAnalyze: editAutoAnalyze,
      pageIds: editPageIds,
      pageGroupIds: editPageGroupIds,
    });
    await service.updateAutomation(editKeyword.keywordId, { analysisPromptIds: editPromptIds });
    setSaving(false);
    setEditDialogOpen(false);
    fetchKeywords();
  };

  const handlePlayClick = (e: React.MouseEvent, kw: Keyword) => {
    e.stopPropagation();
    setConfirmFetchKeyword(kw);
    setConfirmFetchOpen(true);
  };

  const handleConfirmFetch = () => {
    if (!confirmFetchKeyword) return;
    setConfirmFetchOpen(false);
    handleScrape(confirmFetchKeyword);
  };

  const handleScrape = (kw: Keyword) => {
    setScrapeInitialKeyword({
      keyword: kw.keyword,
      scrapeDepth: kw.scrapeDepth,
      keywordId: kw.keywordId,
      startDateMin: kw.startDateMin,
      startDateMax: kw.startDateMax,
    });
    setScrapeDialogOpen(true);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Keywords</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<SettingsOutlinedIcon />} onClick={() => setManagingPages(true)}>
            Advertiser pages
          </Button>
          <Button variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => { setScrapeInitialKeyword(null); setScrapeDialogOpen(true); }}>
            Fetch Ads
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Keyword
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel active={sortField === 'scrapeDepth'} direction={sortField === 'scrapeDepth' ? sortDir : 'asc'} onClick={() => handleSort('scrapeDepth')}>
                  Depth
                </TableSortLabel>
              </TableCell>
              <TableCell>Advertisers</TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'startDateMin'} direction={sortField === 'startDateMin' ? sortDir : 'asc'} onClick={() => handleSort('startDateMin')}>
                  Date Range
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel active={sortField === 'isActive'} direction={sortField === 'isActive' ? sortDir : 'asc'} onClick={() => handleSort('isActive')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel active={sortField === 'autoTranscribe'} direction={sortField === 'autoTranscribe' ? sortDir : 'asc'} onClick={() => handleSort('autoTranscribe')}>
                  Auto Transcribe
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel active={sortField === 'autoAnalyze'} direction={sortField === 'autoAnalyze' ? sortDir : 'asc'} onClick={() => handleSort('autoAnalyze')}>
                  Auto Analyze
                </TableSortLabel>
              </TableCell>
              <TableCell>Prompts</TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'createdAt'} direction={sortField === 'createdAt' ? sortDir : 'asc'} onClick={() => handleSort('createdAt')}>
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'updatedAt'} direction={sortField === 'updatedAt' ? sortDir : 'asc'} onClick={() => handleSort('updatedAt')}>
                  Updated
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keywords.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No keywords yet. Click "Add Keyword" to get started.
                </TableCell>
              </TableRow>
            )}
            {sorted.map((kw) => (
              <TableRow key={kw.keywordId} hover onClick={() => handleRowClick(kw)} sx={{ cursor: 'pointer', ...(kw.isActive ? {} : { opacity: 0.55 }) }}>
                <TableCell>
                  {/* The label makes the list scannable; falls back to the term. */}
                  <Typography variant="body2" fontWeight={500}>{kw.label || kw.keyword}</Typography>
                  {kw.label && (
                    <Typography variant="caption" color="text.secondary">{kw.keyword}</Typography>
                  )}
                </TableCell>
                <TableCell align="center">{kw.scrapeDepth}</TableCell>
                <TableCell>
                  <AdvertiserTargets groups={kw.pageGroups} pages={kw.pages} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {kw.dateRangePreset || formatDateRange(kw.startDateMin, kw.startDateMax)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={kw.isActive ? 'Active' : 'Inactive'}
                    color={kw.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                  <Switch size="small" checked={kw.autoTranscribe}
                    onChange={async (e) => {
                      const val = e.target.checked;
                      setKeywords(prev => prev.map(k => k.keywordId === kw.keywordId ? { ...k, autoTranscribe: val } : k));
                      await service.updateAutomation(kw.keywordId, { autoTranscribe: val });
                    }} />
                </TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                  <Switch size="small" checked={kw.autoAnalyze}
                    onChange={async (e) => {
                      const val = e.target.checked;
                      setKeywords(prev => prev.map(k => k.keywordId === kw.keywordId ? { ...k, autoAnalyze: val } : k));
                      await service.updateAutomation(kw.keywordId, { autoAnalyze: val });
                    }} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {!kw.analysisPromptIds ? 'All' : kw.analysisPromptIds.map(id => prompts.find(p => p.promptId === id)?.name || id.slice(0, 8)).join(', ')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{formatKeywordDate(kw.createdAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{formatKeywordDate(kw.updatedAt)}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {actionLoading[kw.keywordId] ? (
                    <CircularProgress size={20} sx={{ mx: 1 }} />
                  ) : (
                    <>
                      <Tooltip title="Fetch Now">
                        <IconButton size="small" color="primary" onClick={(e) => handlePlayClick(e, kw)}>
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      {kw.isActive ? (
                        <Tooltip title="Deactivate — stop daily fetching">
                          <IconButton size="small" onClick={(e) => handleDeactivateClick(e, kw)}>
                            <PauseCircleIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate — resume daily fetching">
                          <IconButton size="small" color="success" onClick={(e) => handleActivate(e, kw)}>
                            <PlayCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Permanently delete">
                        <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(e, kw)}>
                          <DeleteForeverIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AdvertiserPagesDialog
        open={managingPages}
        onClose={() => setManagingPages(false)}
        onChanged={fetchKeywords}
      />

      {/* Add Keyword Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Keyword</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Keyword"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label="Fetch Depth"
            type="number"
            value={newDepth}
            onChange={(e) => setNewDepth(parseInt(e.target.value, 10) || 20)}
            sx={{ width: 140 }}
            size="small"
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Date Range:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {DATE_PRESETS.map(p => (
                <Chip key={p.label} label={p.label} size="small"
                  color={newDatePreset === p.label ? 'primary' : 'default'}
                  variant={newDatePreset === p.label ? 'filled' : 'outlined'}
                  onClick={() => { setNewDatePreset(p.label); setNewDateFrom(p.from); setNewDateTo(p.to); }}
                />
              ))}
              <Chip label="Custom" size="small"
                color={newDatePreset === 'Custom' ? 'primary' : 'default'}
                variant={newDatePreset === 'Custom' ? 'filled' : 'outlined'}
                onClick={() => setNewDatePreset('Custom')}
              />
            </Box>
            {newDatePreset === 'Custom' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" type="date" label="From" value={newDateFrom}
                  onChange={e => setNewDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To" value={newDateTo}
                  onChange={e => setNewDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newKeyword.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Keyword Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { maxWidth: { lg: '75vw' } } }}>
        <DialogTitle>Edit Keyword</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {/* Keyword + Fetch Depth */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField label="Keyword" value={editKeywordText} onChange={(e) => setEditKeywordText(e.target.value)} fullWidth size="small" />
            <TextField label="Fetch Depth" type="number" value={editDepth} onChange={(e) => setEditDepth(parseInt(e.target.value, 10) || 20)} sx={{ width: 120, flexShrink: 0 }} size="small" />
          </Box>

          <Divider />

          {/* Date Range: presets + picker */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Scrape Date Range</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              {DATE_PRESETS.map(p => (
                <Chip key={p.label} label={p.label} size="small"
                  color={editDatePreset === p.label ? 'primary' : 'default'}
                  variant={editDatePreset === p.label ? 'filled' : 'outlined'}
                  onClick={() => { setEditDatePreset(p.label); setEditDateFrom(p.from); setEditDateTo(p.to); }}
                />
              ))}
              <Chip label="Custom" size="small"
                color={editDatePreset === 'Custom' ? 'primary' : 'default'}
                variant={editDatePreset === 'Custom' ? 'filled' : 'outlined'}
                onClick={() => setEditDatePreset('Custom')}
              />
            </Box>
            {editDatePreset === 'Custom' && (
              <Box sx={{ mt: 1 }}>
                <ControlledDateRangePicker
                  label="Custom Range"
                  width={280}
                  dateRange={[
                    editDateFrom ? dayjs(editDateFrom) : null,
                    editDateTo ? dayjs(editDateTo) : null,
                  ]}
                  onDateRangeChange={(r) => {
                    const from = r[0]?.format('YYYY-MM-DD') || '';
                    const to = r[1]?.format('YYYY-MM-DD') || '';
                    setEditDateFrom(from);
                    setEditDateTo(to);
                    const match = DATE_PRESETS.find(p => p.from === from && p.to === to);
                    if (match) setEditDatePreset(match.label);
                  }}
                />
              </Box>
            )}
          </Box>

          <Divider />

          {/* Automation + Prompts */}
          <Box sx={{ mb: 2 }}>
            <AdvertiserPagePicker
              pageIds={editPageIds}
              pageGroupIds={editPageGroupIds}
              onChange={next => { setEditPageIds(next.pageIds); setEditPageGroupIds(next.pageGroupIds); }}
            />
            <Box sx={{ mt: 1.5 }}>
              <SearchUrlPreview
                url={searchPreview.url}
                loading={searchPreview.loading}
                advertiserCount={searchPreview.advertiserCount}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Automation</Typography>
              <FormControlLabel control={<Switch size="small" checked={editAutoTranscribe} onChange={(e) => setEditAutoTranscribe(e.target.checked)} />} label="Auto Transcribe" />
              <FormControlLabel control={<Switch size="small" checked={editAutoAnalyze} onChange={(e) => setEditAutoAnalyze(e.target.checked)} />} label="Auto Analyze" />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Analysis Prompts ({!editPromptIds ? 'All' : `${editPromptIds.length} selected`})
              </Typography>
              {prompts.map(p => {
                const allSelected = !editPromptIds;
                const checked = allSelected || editPromptIds!.includes(p.promptId);
                return (
                  <FormControlLabel key={p.promptId} label={<Typography variant="body2">{p.name}</Typography>}
                    control={<Checkbox size="small" checked={checked}
                      onChange={(e) => {
                        if (allSelected) {
                          setEditPromptIds(prompts.filter(pp => pp.promptId !== p.promptId).map(pp => pp.promptId));
                        } else if (e.target.checked) {
                          const next = [...editPromptIds!, p.promptId];
                          setEditPromptIds(next.length >= prompts.length ? null : next);
                        } else {
                          setEditPromptIds(editPromptIds!.filter(id => id !== p.promptId));
                        }
                      }} />}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Fetched Ads Table */}
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Fetched Ads ({editAdsTotal})
            </Typography>
            {editAdsLoading && editAdsAll.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : editAdsAll.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No ads fetched yet for this keyword.
              </Typography>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {([
                          { field: 'pageName' as keyof ScrapedAd, label: 'Page' },
                          { field: 'headline' as keyof ScrapedAd, label: 'Headline' },
                          { field: 'impressionRank' as keyof ScrapedAd, label: 'Rank', align: 'center' as const },
                          { field: 'videoUrl' as keyof ScrapedAd, label: 'Video', align: 'center' as const },
                          { field: 'startDate' as keyof ScrapedAd, label: 'Start Date' },
                          { field: 'scrapedAt' as keyof ScrapedAd, label: 'Fetched' },
                        ]).map(col => (
                          <TableCell key={col.field} align={col.align}>
                            <TableSortLabel
                              active={editAdsSortField === col.field}
                              direction={editAdsSortField === col.field ? editAdsSortDir : 'asc'}
                              onClick={() => { handleEditAdsSort(col.field); setEditAdsPage(0); }}
                            >
                              {col.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedEditAds.map((ad) => (
                        <TableRow key={ad.adId} hover sx={{ cursor: 'pointer' }} onClick={() => setViewAdId(ad.adId)}>
                          <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ad.pageName || '—'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ad.headline || ad.caption?.slice(0, 60) || '—'}
                          </TableCell>
                          <TableCell align="center">{ad.impressionRank || '—'}</TableCell>
                          <TableCell align="center">
                            <Chip label={ad.videoUrl ? 'Yes' : 'No'} size="small" variant="outlined"
                              color={ad.videoUrl ? 'primary' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
                          </TableCell>
                          <TableCell>{ad.startDate || '—'}</TableCell>
                          <TableCell>{ad.scrapedAt ? new Date(ad.scrapedAt).toLocaleDateString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={editAdsTotal}
                  page={editAdsPage}
                  onPageChange={handleEditAdsPageChange}
                  onRowsPerPageChange={handleEditAdsRowsPerPageChange}
                  rowsPerPage={editAdsRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  size="small"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={!editKeywordText.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Fetch Dialog */}
      <Dialog open={confirmFetchOpen} onClose={() => setConfirmFetchOpen(false)} maxWidth="xs">
        <DialogTitle>Fetch Ads Now?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Start fetching ads for "{confirmFetchKeyword?.keyword}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFetchOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmFetch}>Fetch</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Deactivate Dialog */}
      <Dialog open={confirmDeactivateOpen} onClose={() => setConfirmDeactivateOpen(false)} maxWidth="xs">
        <DialogTitle>Deactivate Keyword?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will stop daily fetching for "{confirmDeactivateKeyword?.keyword}".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivateOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmDeactivate}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs">
        <DialogTitle>Permanently Delete Keyword?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently deletes "{confirmDeleteKeyword?.label || confirmDeleteKeyword?.keyword}" and cannot be undone.
            Fetched ads are not affected.
          </Typography>
          {/* Keywords are shared, so this removes it from the other tool too —
              worth saying before the click, not after. */}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Keywords are shared: this also removes it from <strong>Static Creative</strong>.
            {deleteUsage && (
              <> It has pulled {deleteUsage.videoAdCount} video and {deleteUsage.staticAdCount} static ads,
              which stay in their libraries.</>
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Scrape Progress Dialog */}
      <ScrapeProgressDialog
        open={scrapeDialogOpen}
        onClose={() => setScrapeDialogOpen(false)}
        onKeywordSaved={fetchKeywords}
        initialKeyword={scrapeInitialKeyword}
      />

      {/* Ad Detail Dialog */}
      <AdDetailDialog
        adId={viewAdId}
        onClose={() => setViewAdId(null)}
        onRefresh={() => editKeyword && fetchEditAdsBatch(editKeyword.keywordId, 0, true)}
      />
    </Box>
  );
}
